import React from 'react';

import {
  BrandSafetyRuleActiveActive,
  BrandSafetyRuleTargetingAdomainExceptionStateExactMatch,
  BrandSafetyRuleTargetingTypeBidder,
  BrandSafetyRuleTargetingTypeChannel,
  BrandSafetyRuleTargetingTypeGlobal,
  IBrandSafetyRule,
  IBrandSafetyRuleAssociationTable,
  IBrandSafetyRuleException,
} from '~/src/dash/api/brand-safety-rules';
import {
  EAllowStatus,
  ICountryGroup,
  IUser,
  TargetingCCPAAll,
  TargetingCoppaAll,
  TargetingLogicOR,
} from '~/src/dash/api/console-api';
import { EBool } from '~/src/dash/api/types/bool';
import { IStore } from '~/src/dash/reducers/store';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import {
  IS_AUTHORIZED,
  notAllowedEmptyIPermissionCheck,
} from '~/src/dash/utils/permission_service';
import { BrandSafetyRuleEdit } from '~/src/dash/views/brand-safety-rule-edit';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IHistoryProps,
    IUserStateProps {}

// gets the default rule targeting a user can create (look at what's possible in permissions)
function getDefaultRuleTargeting(user: IUser): number {
  const hasPublisherWriteRight =
    notAllowedEmptyIPermissionCheck; /* TODO uncomment to release - IS_AUTHORIZED(['write_publisher_brandsafety_rule'], user)*/
  const hasChannelWriteRight = IS_AUTHORIZED(['write_channel_brandsafety_rule'], user);

  // global
  if (hasPublisherWriteRight.isAuthorized) {
    return BrandSafetyRuleTargetingTypeGlobal;
  }
  // channel
  if (hasChannelWriteRight.isAuthorized) {
    return BrandSafetyRuleTargetingTypeChannel;
    // bidder
  }
  return BrandSafetyRuleTargetingTypeBidder;
}

export const BrandSafetyNewView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
  },
  function ({ session, user }: IProps) {
    // create a new empty rule with default values
    const newRule: IBrandSafetyRule = {
      ID: 0,
      CreatedAt: '',
      UpdatedAt: '',
      DeletedAt: '',
      PublisherID: Number(session.publisher.ID),
      Name: 'New Block Rule',
      Active: BrandSafetyRuleActiveActive,
      RuleTargetingType: getDefaultRuleTargeting(user.user),
      NbrResponseBlocked: 0,
      TargetedIABs: '',
      TargetedAdomains: '',
      TargetingAdomainExceptionState: BrandSafetyRuleTargetingAdomainExceptionStateExactMatch,
      TargetedMediafiles: '',
      TargetedCreativeIDs: '',
      BrandSafetyRuleExceptions: [] as IBrandSafetyRuleException[],
      TargetedObjects: [] as IBrandSafetyRuleAssociationTable[],
      IsTargetingExcluded: false,
      IsTypeTargetingExcluded: true,
      EnableSaveCreativeValidations: EBool.EBool_FALSE,
      EnableBlockBidsWithEmptyIABCatAdomain: EBool.EBool_FALSE,

      // Targeted Content
      TargetedContentCategory: '',
      TargetedContentCategoryIsAllowed: EAllowStatus.ALLOW,
      TargetedContentChannel: '',
      TargetedContentChannelIsAllowed: EAllowStatus.ALLOW,
      TargetedContentDistributor: '',
      TargetedContentDistributorIsAllowed: EAllowStatus.ALLOW,
      TargetedContentGenre: '',
      TargetedContentGenreIsAllowed: EAllowStatus.ALLOW,
      TargetedContentID: '',
      TargetedContentIDIsAllowed: EAllowStatus.ALLOW,
      TargetedContentNetwork: '',
      TargetedContentNetworkIsAllowed: EAllowStatus.ALLOW,
      TargetedContentProducer: '',
      TargetedContentProducerIsAllowed: EAllowStatus.ALLOW,
      TargetedContentRating: '',
      TargetedContentRatingIsAllowed: EAllowStatus.ALLOW,
      TargetedContentSeries: '',
      TargetedContentSeriesIsAllowed: EAllowStatus.ALLOW,
      TargetedContentTitle: '',
      TargetedContentTitleIsAllowed: EAllowStatus.ALLOW,

      // targeting app/site
      TargetedAppBundleID: '',
      TargetedAppBundleIDIsAllowed: EAllowStatus.ALLOW,
      TargetedSitePages: '',
      TargetedSitePagesIsAllowed: EAllowStatus.ALLOW,

      // targeting other
      TargetedVenueID: '',
      TargetedVenueIDIsAllowed: EAllowStatus.ALLOW,
      TargetedCoppa: TargetingCoppaAll,

      // Targeted Custom
      TargetedCustom4: '',
      TargetedCustom4IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom5: '',
      TargetedCustom5IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom6: '',
      TargetedCustom6IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom7: '',
      TargetedCustom7IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom8: '',
      TargetedCustom8IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom9: '',
      TargetedCustom9IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom10: '',
      TargetedCustom10IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom11: '',
      TargetedCustom11IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom12: '',
      TargetedCustom12IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom13: '',
      TargetedCustom13IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom14: '',
      TargetedCustom14IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom15: '',
      TargetedCustom15IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom16: '',
      TargetedCustom16IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom17: '',
      TargetedCustom17IsAllowed: EAllowStatus.ALLOW,
      TargetedCustom18: '',
      TargetedCustom18IsAllowed: EAllowStatus.ALLOW,
      // TODO: add migration for custom targeting in brand safety
      // TargetedCustomTargeting: '',
      // TargetedCustomTargetingIsAllowed: EAllowStatus.ALLOW,

      // Targeted Geo
      TargetingCountryGroupIDs: '',
      TargetingCountryGroupIDsIsAllowed: EAllowStatus.ALLOW,
      TargetingDeviceCountry: '',
      TargetingDeviceCountryIsAllowed: EAllowStatus.ALLOW,
      LocalTargetingAllowed: '',
      LocalTargetingDisallowed: '',
      LocalTargetingAllowedLogic: TargetingLogicOR,
      LocalTargetingDisallowedLogic: TargetingLogicOR,

      CountryGroups: [] as ICountryGroup[],

      TargetedLanguages: '',
      TargetingCCPA: TargetingCCPAAll,
    };
    // display the edit form for this new rule
    return (
      <div>
        <BrandSafetyRuleEdit rule={newRule} error={''} />
      </div>
    );
  },
);
